import { CombinedGraphQLErrors } from "@apollo/client/errors";
import type { GraphQLFormattedError } from "graphql";
import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { AlertProps } from "@mui/material/Alert";
import { isDreamError } from "@portal/app/_common/lib/errorHelpers";

export interface ClientErrorMessage {
  title: string;
  description: React.ReactNode;
  severity?: AlertProps["severity"];
}

/** Apollo 4 exposes GraphQL errors together and transport errors directly. */
export const getGraphQLErrors = (error: unknown): readonly GraphQLFormattedError[] =>
  CombinedGraphQLErrors.is(error) ? error.errors : [];

export const detectKnownClientError = (error: unknown): ClientFacingErrors | undefined => {
  if (isDreamError(error)) return error.extensions.code;
  if (CombinedGraphQLErrors.is(error)) {
    const codes = error.errors.map((item) => item.extensions?.code);
    return Object.values(ClientFacingErrors).find((code) => codes.includes(code));
  }
  if (error && typeof error === "object" && "extensions" in error) {
    const code = (error as GraphQLFormattedError).extensions?.code;
    return typeof code === "string"
      ? Object.values(ClientFacingErrors).find((known) => code.includes(known))
      : undefined;
  }
};

/**
 * Returns true when the error is one DreamSearch query consumers should
 * degrade gracefully on by falling back to a known-good ZIP: the user's ZIP
 * was unrecognized (INVALID_ADDRESS) or geocoding was temporarily unavailable
 * (GEOCODE_UNAVAILABLE, e.g. a Google Maps timeout — ENG-7782). The fallback
 * behavior is identical for both; the codes stay distinct so monitoring can
 * tell an outage apart from bad input.
 */
export const isDegradableGeocodeError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }
  const code = detectKnownClientError(error);
  return (
    code === ClientFacingErrors.InvalidAddress || code === ClientFacingErrors.GeocodeUnavailable
  );
};

/** Superseding a lazy query or unmounting its owner aborts its execute promise. */
export const isQueryAbortError = (error: unknown): boolean =>
  !!error && typeof error === "object" && "name" in error && error.name === "AbortError";
